import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Alert,
  Tooltip,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Autocomplete,
  TablePagination,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as PostIcon,
  Cancel as UnpostIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useConfirm } from '../hooks/useConfirm';
import { journalEntriesService, JournalEntryDto } from '../services/journalEntries.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import apiClient from '../services/api';
import JournalEntryDialog from '../components/JournalEntry/JournalEntryDialog';
import JournalEntryDetailsDialog from '../components/JournalEntry/JournalEntryDetailsDialog';
import JournalEntryPreviewDialog from '../components/JournalEntry/JournalEntryPreviewDialog';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const JournalEntriesPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const confirm = useConfirm();

  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const t = {
    title: isRTL ? 'قيود اليومية' : 'Journal Entries',
    refresh: isRTL ? 'تحديث' : 'Refresh',
    newEntry: isRTL ? 'قيد جديد' : 'New Entry',
    fromDate: isRTL ? 'من تاريخ' : 'From Date',
    toDate: isRTL ? 'إلى تاريخ' : 'To Date',
    status: isRTL ? 'الحالة' : 'Status',
    all: isRTL ? 'الكل' : 'All',
    draft: isRTL ? 'مسودة' : 'Draft',
    posted: isRTL ? 'مرحل' : 'Posted',
    cancelled: isRTL ? 'ملغي' : 'Cancelled',
    entryNumber: isRTL ? 'رقم القيد' : 'Entry No',
    date: isRTL ? 'التاريخ' : 'Date',
    description: isRTL ? 'الوصف' : 'Description',
    debit: isRTL ? 'المدين' : 'Debit',
    credit: isRTL ? 'الدائن' : 'Credit',
    fiscalYear: isRTL ? 'الفترة المالية' : 'Fiscal Period',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    loading: isRTL ? 'جاري التحميل...' : 'Loading...',
    noEntries: isRTL ? 'لا توجد قيود' : 'No entries found',
    view: isRTL ? 'عرض' : 'View',
    edit: isRTL ? 'تعديل' : 'Edit',
    delete: isRTL ? 'حذف' : 'Delete',
    post: isRTL ? 'ترحيل' : 'Post',
    unpost: isRTL ? 'إلغاء الترحيل' : 'Unpost',
    print: isRTL ? 'طباعة' : 'Print',
    search: isRTL ? 'بحث' : 'Search',
    searchPlaceholder: isRTL ? 'رقم القيد أو الوصف' : 'Entry No or Description',
    totalEntries: isRTL ? 'إجمالي القيود' : 'Total Entries',
    draftEntries: isRTL ? 'مسودات' : 'Draft',
    postedEntries: isRTL ? 'مرحل' : 'Posted',
    totalAmount: isRTL ? 'إجمالي (د.ك)' : 'Total (KWD)',
    confirmDelete: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?',
    confirmPost: isRTL ? 'هل تريد ترحيل القيد؟' : 'Do you want to post this entry?',
    confirmUnpost: isRTL ? 'هل تريد إلغاء الترحيل؟' : 'Do you want to unpost this entry?',
    cannotEditPostedEntry: isRTL ? 'لا يمكن تعديل قيد مرحل' : 'Cannot edit posted entry',
    cannotDeletePostedEntry: isRTL ? 'لا يمكن حذف قيد مرحل' : 'Cannot delete posted entry',
    entryDeletedSuccessfully: isRTL ? 'تم حذف القيد بنجاح' : 'Entry deleted successfully',
    entryPostedSuccessfully: isRTL ? 'تم ترحيل القيد بنجاح' : 'Entry posted successfully',
    entryUnpostedSuccessfully: isRTL ? 'تم إلغاء الترحيل بنجاح' : 'Entry unposted successfully',
    entryCreatedSuccessfully: isRTL ? 'تم إنشاء القيد بنجاح' : 'Entry created successfully',
    entryUpdatedSuccessfully: isRTL ? 'تم تحديث القيد بنجاح' : 'Entry updated successfully',
    failedToLoadEntries: isRTL ? 'فشل تحميل القيود' : 'Failed to load entries',
    failedToDeleteEntry: isRTL ? 'فشل حذف القيد' : 'Failed to delete entry',
    failedToPostEntry: isRTL ? 'فشل ترحيل القيد' : 'Failed to post entry',
    failedToUnpostEntry: isRTL ? 'فشل إلغاء الترحيل' : 'Failed to unpost entry',
    rowsPerPage: isRTL ? 'عدد الصفوف:' : 'Rows per page:'
  };

  const [entries, setEntries] = useState<JournalEntryDto[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntryDto | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterBranch, setFilterBranch] = useState('');
  const [branches, setBranches] = useState<{branchId: number, branchName: string, branchNameAr?: string}[]>([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    posted: 0,
    totalAmount: 0
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadEntries();
    loadCompanyInfo();
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const response = await apiClient.get('/Branches');
      setBranches(response.data);
    } catch { }
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (err) {
      console.error('Failed to load company info:', err);
    }
  };

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [entries, searchTerm, filterStartDate, filterEndDate, filterStatus, filterBranch]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await journalEntriesService.getAll();
      const sorted = Array.isArray(data) ? [...data].sort((a, b) => b.journalEntryId - a.journalEntryId) : [];
      setEntries(sorted);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t.failedToLoadEntries, 'error');
      console.error('Failed to load journal entries:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!Array.isArray(entries)) {
      setFilteredEntries([]);
      return;
    }

    let filtered = [...entries];

    if (filterStatus !== 'All') {
      filtered = filtered.filter(e => e.status === filterStatus);
    }

    // Compare only the date portion (YYYY-MM-DD) to avoid time-of-day
    // making "2026-05-16T00:00:00" > "2026-05-16" in string comparison.
    if (filterStartDate) {
      filtered = filtered.filter(e => (e.entryDate || '').substring(0, 10) >= filterStartDate);
    }

    if (filterEndDate) {
      filtered = filtered.filter(e => (e.entryDate || '').substring(0, 10) <= filterEndDate);
    }

    if (filterBranch) {
      filtered = filtered.filter(e =>
        (e.description && e.description.toLowerCase().includes(filterBranch.toLowerCase())) ||
        (e.descriptionAr && e.descriptionAr.toLowerCase().includes(filterBranch.toLowerCase())) ||
        (e.details && e.details.some((d: any) => d.costCenterName && d.costCenterName.toLowerCase().includes(filterBranch.toLowerCase())))
      );
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        e.entryNumber.toLowerCase().includes(search) ||
        (e.description && e.description.toLowerCase().includes(search)) ||
        (e.descriptionAr && e.descriptionAr.toLowerCase().includes(search))
      );
    }

    setFilteredEntries(filtered);
    setPage(0);
  };

  const calculateStats = () => {
    if (!Array.isArray(entries)) return;
    const total = entries.length;
    const draft = entries.filter(e => e.status === 'Draft').length;
    const posted = entries.filter(e => e.status === 'Posted').length;
    const totalAmount = entries.reduce((sum, e) => sum + e.totalDebit, 0);
    setStats({ total, draft, posted, totalAmount });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreate = () => {
    setSelectedEntry(null);
    setCreateDialogOpen(true);
  };

  const handleEdit = (entry: JournalEntryDto) => {
    if (entry.status === 'Posted') {
      showSnackbar(t.cannotEditPostedEntry, 'error');
      return;
    }
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleView = (entry: JournalEntryDto) => {
    setSelectedEntry(entry);
    setDetailsDialogOpen(true);
  };

  const handlePrint = (entry: JournalEntryDto) => {
    setSelectedEntry(entry);
    setPreviewDialogOpen(true);
  };

  const handleDelete = async (entry: JournalEntryDto) => {
    if (entry.status === 'Posted') {
      showSnackbar(t.cannotDeletePostedEntry, 'error');
      return;
    }
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      setLoading(true);
      await journalEntriesService.delete(entry.journalEntryId);
      showSnackbar(t.entryDeletedSuccessfully, 'success');
      await loadEntries();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t.failedToDeleteEntry, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (entry: JournalEntryDto) => {
    if (entry.status === 'Posted') {
      showSnackbar(isRTL ? 'القيد مرحّل بالفعل' : 'Entry already posted', 'info');
      return;
    }
    const ok = await confirm({ message: t.confirmPost, severity: 'warning' });
    if (!ok) return;
    try {
      setLoading(true);
      await journalEntriesService.post(entry.journalEntryId);
      showSnackbar(t.entryPostedSuccessfully, 'success');
      await loadEntries();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t.failedToPostEntry, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpost = async (entry: JournalEntryDto) => {
    if (entry.status === 'Draft') {
      showSnackbar(isRTL ? 'القيد غير مرحّل' : 'Entry not posted', 'info');
      return;
    }
    const ok = await confirm({ message: t.confirmUnpost, severity: 'warning' });
    if (!ok) return;
    try {
      setLoading(true);
      await journalEntriesService.unpost(entry.journalEntryId);
      showSnackbar(t.entryUnpostedSuccessfully, 'success');
      await loadEntries();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t.failedToUnpostEntry, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    showSnackbar(t.entryCreatedSuccessfully, 'success');
    setCreateDialogOpen(false);
    loadEntries();
  };

  const handleEditSuccess = () => {
    showSnackbar(t.entryUpdatedSuccessfully, 'success');
    setEditDialogOpen(false);
    loadEntries();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Posted': return 'success';
      case 'Draft': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Posted': return t.posted;
      case 'Draft': return t.draft;
      case 'Cancelled': return t.cancelled;
      default: return status;
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');

  const exportToExcel = async () => {
    if (filteredEntries.length === 0) return;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(isRTL ? 'قيود اليومية' : 'Journal Entries');

    // Company logo
    if (companyInfo?.logoBase64) {
      try {
        const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
        ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 60, height: 60 } });
      } catch (e) {}
    }

    // Company name
    ws.mergeCells('B1:J1');
    ws.getCell('B1').value = isRTL ? companyInfo?.companyNameAr || companyInfo?.companyName : companyInfo?.companyName || '';
    ws.getCell('B1').font = { size: 14, bold: true };
    ws.getCell('B1').alignment = { horizontal: 'center' };

    // Report title
    ws.mergeCells('B2:J2');
    ws.getCell('B2').value = isRTL ? 'قيود اليومية' : 'Journal Entries';
    ws.getCell('B2').font = { size: 12, bold: true };
    ws.getCell('B2').alignment = { horizontal: 'center' };

    // Generated date
    ws.mergeCells('B3:J3');
    ws.getCell('B3').value = `${isRTL ? 'تاريخ التقرير' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}`;
    ws.getCell('B3').alignment = { horizontal: 'center' };
    ws.addRow([]);

    // Header row
    const headers = isRTL
      ? ['رقم القيد', 'التاريخ', 'الوصف', 'كود الحساب', 'اسم الحساب', 'مركز التكلفة', 'مدين', 'دائن', 'الحالة', 'الفترة المالية']
      : ['Entry No', 'Date', 'Description', 'Account Code', 'Account Name', 'Cost Center', 'Debit', 'Credit', 'Status', 'Fiscal Period'];

    const hRow = ws.addRow(headers);
    hRow.eachCell(c => {
      c.font = { bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '5667EA' } };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.border = {
        top: { style: 'thin' }, bottom: { style: 'thin' },
        left: { style: 'thin' }, right: { style: 'thin' }
      };
    });
    hRow.height = 20;

    // Data rows — one row per detail line
    let rowIndex = 0;
    filteredEntries.forEach(entry => {
      if (entry.details && entry.details.length > 0) {
        entry.details.forEach((detail, i) => {
          const accountName = isRTL ? detail.accountNameAr || detail.accountName : detail.accountName;
          const r = ws.addRow([
            entry.entryNumber,
            formatDate(entry.entryDate),
            isRTL ? entry.descriptionAr || entry.description || '' : entry.description || '',
            detail.accountCode,
            accountName,
            detail.costCenterCode || '',
            detail.debit > 0 ? detail.debit : null,
            detail.credit > 0 ? detail.credit : null,
            i === 0 ? getStatusLabel(entry.status) : '',
            i === 0 ? entry.fiscalPeriodName || '' : ''
          ]);
          r.eachCell((c, idx) => {
            c.border = {
              top: { style: 'thin' }, bottom: { style: 'thin' },
              left: { style: 'thin' }, right: { style: 'thin' }
            };
            if (idx === 7 || idx === 8) {
              c.numFmt = '#,##0.000';
              c.alignment = { horizontal: 'right' };
              if (idx === 7 && detail.debit > 0) c.font = { color: { argb: '2E7D32' } };
              if (idx === 8 && detail.credit > 0) c.font = { color: { argb: 'C62828' } };
            }
          });
          if (rowIndex % 2 === 0) {
            r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F5F5FF' } }; });
          }
        });
      } else {
        const r = ws.addRow([
          entry.entryNumber,
          formatDate(entry.entryDate),
          isRTL ? entry.descriptionAr || entry.description || '' : entry.description || '',
          '', '',  '',
          entry.totalDebit > 0 ? entry.totalDebit : null,
          entry.totalCredit > 0 ? entry.totalCredit : null,
          getStatusLabel(entry.status),
          entry.fiscalPeriodName || ''
        ]);
        r.eachCell((c, idx) => {
          c.border = {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
          };
          if (idx === 7 || idx === 8) { c.numFmt = '#,##0.000'; c.alignment = { horizontal: 'right' }; }
        });
        if (rowIndex % 2 === 0) {
          r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F5F5FF' } }; });
        }
      }
      rowIndex++;
    });

    // Totals row
    const totalDebit = filteredEntries.reduce((s, e) => s + e.totalDebit, 0);
    const totalCredit = filteredEntries.reduce((s, e) => s + e.totalCredit, 0);
    const totRow = ws.addRow([
      isRTL ? 'الإجمالي' : 'Total',
      '', '', '', '', '',
      totalDebit, totalCredit, '', ''
    ]);
    totRow.eachCell((c, idx) => {
      c.font = { bold: true };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD54F' } };
      c.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
      if (idx === 7 || idx === 8) { c.numFmt = '#,##0.000'; c.alignment = { horizontal: 'right' }; }
    });

    ws.columns = [
      { width: 16 }, { width: 12 }, { width: 35 }, { width: 14 },
      { width: 30 }, { width: 18 }, { width: 14 }, { width: 14 },
      { width: 12 }, { width: 18 }
    ];

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `JournalEntries_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
  };

  const exportToPDF = () => {
    if (filteredEntries.length === 0) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();

    if (companyInfo?.logoBase64) {
      try { doc.addImage(companyInfo.logoBase64, 'PNG', 10, 5, 18, 18); } catch (e) {}
    }

    doc.setFontSize(13);
    doc.text(
      isRTL ? companyInfo?.companyNameAr || companyInfo?.companyName || '' : companyInfo?.companyName || '',
      pw / 2, 10, { align: 'center' }
    );
    doc.setFontSize(11);
    doc.text(isRTL ? 'قيود اليومية' : 'Journal Entries', pw / 2, 17, { align: 'center' });
    doc.setFontSize(8);
    doc.text(
      `${isRTL ? 'تاريخ التقرير' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}  |  ${isRTL ? 'عدد القيود' : 'Entries'}: ${filteredEntries.length}`,
      pw / 2, 23, { align: 'center' }
    );

    const body: string[][] = [];
    filteredEntries.forEach(entry => {
      if (entry.details && entry.details.length > 0) {
        entry.details.forEach((detail, i) => {
          const accountName = isRTL ? detail.accountNameAr || detail.accountName : detail.accountName;
          body.push([
            i === 0 ? entry.entryNumber : '',
            i === 0 ? formatDate(entry.entryDate) : '',
            i === 0 ? (isRTL ? entry.descriptionAr || entry.description || '' : entry.description || '') : '',
            `${detail.accountCode}\n${accountName || ''}`,
            detail.costCenterCode || '-',
            detail.debit > 0 ? detail.debit.toFixed(3) : '-',
            detail.credit > 0 ? detail.credit.toFixed(3) : '-',
            i === 0 ? getStatusLabel(entry.status) : '',
            i === 0 ? entry.fiscalPeriodName || '' : ''
          ]);
        });
      } else {
        body.push([
          entry.entryNumber,
          formatDate(entry.entryDate),
          isRTL ? entry.descriptionAr || entry.description || '' : entry.description || '',
          '', '-',
          entry.totalDebit > 0 ? entry.totalDebit.toFixed(3) : '-',
          entry.totalCredit > 0 ? entry.totalCredit.toFixed(3) : '-',
          getStatusLabel(entry.status),
          entry.fiscalPeriodName || ''
        ]);
      }
    });

    const totalDebit = filteredEntries.reduce((s, e) => s + e.totalDebit, 0);
    const totalCredit = filteredEntries.reduce((s, e) => s + e.totalCredit, 0);
    body.push([
      isRTL ? 'الإجمالي' : 'Total', '', '', '', '',
      totalDebit.toFixed(3), totalCredit.toFixed(3), '', ''
    ]);

    autoTable(doc, {
      head: [[
        isRTL ? 'رقم القيد' : 'Entry No',
        isRTL ? 'التاريخ' : 'Date',
        isRTL ? 'الوصف' : 'Description',
        isRTL ? 'الحساب' : 'Account',
        isRTL ? 'مركز التكلفة' : 'Cost Center',
        isRTL ? 'مدين' : 'Debit',
        isRTL ? 'دائن' : 'Credit',
        isRTL ? 'الحالة' : 'Status',
        isRTL ? 'الفترة' : 'Period'
      ]],
      body,
      startY: 28,
      styles: { fontSize: 7.5, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [86, 103, 234], fontStyle: 'bold', textColor: 255 },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 20 },
        2: { cellWidth: 48 },
        3: { cellWidth: 48 },
        4: { cellWidth: 22 },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 22, halign: 'right' },
        7: { cellWidth: 18 },
        8: { cellWidth: 24 }
      },
      didParseCell: (data) => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [255, 213, 79];
          data.cell.styles.fontStyle = 'bold';
        }
      },
      alternateRowStyles: { fillColor: [245, 245, 255] },
    });

    doc.save(`JournalEntries_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const paginatedEntries = filteredEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalEntries}</Typography>
                </Box>
                <AccountBalanceIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.draft}</Typography>
                  <Typography variant="body2">{t.draftEntries}</Typography>
                </Box>
                <PendingActionsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.posted}</Typography>
                  <Typography variant="body2">{t.postedEntries}</Typography>
                </Box>
                <PostIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.totalAmount.toFixed(3)}</Typography>
                  <Typography variant="body2">{t.totalAmount}</Typography>
                </Box>
                <MonetizationOnIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header & Actions */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountBalanceIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadEntries}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'تصدير Excel' : 'Export Excel'}>
              <span>
                <IconButton
                  color="success"
                  onClick={exportToExcel}
                  disabled={filteredEntries.length === 0}
                  sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.18)' } }}
                >
                  <ExcelIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isRTL ? 'تصدير PDF' : 'Export PDF'}>
              <span>
                <IconButton
                  color="error"
                  onClick={exportToPDF}
                  disabled={filteredEntries.length === 0}
                  sx={{ bgcolor: 'rgba(211, 47, 47, 0.08)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.18)' } }}
                >
                  <PdfIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
              }}
            >
              {t.newEntry}
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            placeholder={t.searchPlaceholder}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={filterStartDate ? new Date(filterStartDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFilterStartDate(newVal);
                if (filterEndDate && newVal && filterEndDate < newVal) setFilterEndDate('');
              }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={filterEndDate ? new Date(filterEndDate) : maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 180 } } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={filterEndDate ? new Date(filterEndDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFilterEndDate(newVal);
                if (filterStartDate && newVal && filterStartDate > newVal) setFilterStartDate('');
              }}
              format="dd/MM/yyyy"
              minDate={filterStartDate ? new Date(filterStartDate) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 180 } } }}
            />
          </LocalizationProvider>
          <TextField
            select
            label={t.status}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="All">{t.all}</MenuItem>
            <MenuItem value="Draft">{t.draft}</MenuItem>
            <MenuItem value="Posted">{t.posted}</MenuItem>
            <MenuItem value="Cancelled">{t.cancelled}</MenuItem>
          </TextField>
          <Autocomplete
            size="small"
            options={branches}
            getOptionLabel={(o) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
            value={branches.find(b => b.branchName === filterBranch) || null}
            onChange={(_, v) => setFilterBranch(v?.branchName || '')}
            renderInput={(params) => <TextField {...params} label={isRTL ? 'الفرع' : 'Branch'} />}
            sx={{ width: 200 }}
          />
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.entryNumber}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.date}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.description}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.debit}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.credit}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.fiscalYear}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress sx={{ color: '#667eea' }} />
                </TableCell>
              </TableRow>
            ) : paginatedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{t.noEntries}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => {
                const isDraft = entry.status === 'Draft';
                return (
                  <TableRow key={entry.journalEntryId} hover sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}>
                    <TableCell>
                      <Typography fontWeight="bold" color="#667eea">{entry.entryNumber}</Typography>
                    </TableCell>
                    <TableCell>{new Date(entry.entryDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {isRTL ? entry.descriptionAr || entry.description : entry.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="#4caf50">{entry.totalDebit.toFixed(3)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="#ff9800">{entry.totalCredit.toFixed(3)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {entry.fiscalPeriodName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(entry.status)}
                        color={getStatusColor(entry.status)}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t.view}>
                          <IconButton size="small" onClick={() => handleView(entry)} sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' } }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.print}>
                          <IconButton size="small" onClick={() => handlePrint(entry)} sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' } }}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isDraft && (
                          <>
                            <Tooltip title={t.edit}>
                              <IconButton size="small" onClick={() => handleEdit(entry)} sx={{ color: 'warning.main', '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.1)' } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t.delete}>
                              <IconButton size="small" onClick={() => handleDelete(entry)} sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canPost && (
                            <Tooltip title={t.post}>
                              <IconButton size="small" onClick={() => handlePost(entry)} sx={{ color: 'success.main', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.1)' } }}>
                                <PostIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            )}
                          </>
                        )}
                        {!isDraft && canPost && (
                          <Tooltip title={t.unpost}>
                            <IconButton size="small" onClick={() => handleUnpost(entry)} sx={{ color: 'warning.main', '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.1)' } }}>
                              <UnpostIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50, { label: t.all, value: -1 }]}
          component="div"
          count={filteredEntries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
          }
        />
      </TableContainer>

      {/* Dialogs */}
      {createDialogOpen && (
        <JournalEntryDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {editDialogOpen && selectedEntry && (
        <JournalEntryDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          entry={selectedEntry}
        />
      )}

      {detailsDialogOpen && selectedEntry && (
        <JournalEntryDetailsDialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          entry={selectedEntry}
        />
      )}

      {previewDialogOpen && selectedEntry && (
        <JournalEntryPreviewDialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          entry={selectedEntry}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JournalEntriesPage;